/**
 * The Admin route an invitation link points at: `/invite/<token>`.
 *
 * Kept in its own module and unit-tested for exactly the reason
 * `selectOrgPath.ts` is: this route is reached by someone clicking a link in an
 * email, with NO session, so it is matched against `window.location.pathname`
 * before the router mounts. A miss here does not fail loudly — it falls through
 * to the sign-in screen, and the recipient is asked to sign in to an account
 * they do not have yet. That is a dead end that looks like a working page.
 *
 * WHERE THE TOKEN LIVES, AND WHY IT MOVED. It is now in the URL FRAGMENT —
 * `/invite#<token>` — because a fragment is never sent to a server. The old
 * shape was a path segment, `/invite/<token>`, which put a live bearer
 * credential in the request line of every hop: `apps/admin/nginx.conf`
 * declared no `access_log`, so the admin tier inherited the `nginx:alpine`
 * default that logs `$request` verbatim, and `invite` is absent from that
 * file's API-family regex so the request never reached the API where the
 * logging controls live. Referrer-Policy here is strict-origin-when-cross-
 * origin, so the token was repeated in `Referer` on every asset the screen
 * loaded, too.
 *
 * THE PATH FORM IS STILL ACCEPTED, deliberately. Every invitation already sent
 * carries it, and those links have to keep working for the rest of their TTL —
 * breaking them would lock out exactly the people who cannot ask for help
 * through the product because they are not in it yet. `email/delivery.ts` no
 * longer MINTS that shape, and `apps/admin/nginx.conf` redacts it out of the
 * access log for as long as it is in circulation.
 *
 * The token must survive `encodeURIComponent` round-tripping. It is base64url
 * (`A-Za-z0-9_-`), which is URL-safe by construction, but the parse still
 * decodes: a host or mail client that percent-encodes it must not change what
 * we send to the API.
 */
export const INVITE_PATH_PREFIX = '/invite';

/** True when this pathname is an invitation landing, with or without a token. */
export function isInvitePath(pathname: string): boolean {
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed === INVITE_PATH_PREFIX || trimmed.startsWith(`${INVITE_PATH_PREFIX}/`);
}

/**
 * Extract the token from an invitation pathname.
 *
 * Returns null for a bare `/invite` with no token, and for anything with extra
 * path segments — `/invite/abc/def` is not a link we generated, and guessing
 * which segment was meant would be inventing intent.
 */
export function inviteTokenFrom(pathname: string): string | null {
  if (!isInvitePath(pathname)) return null;
  const rest = pathname.replace(/\/+$/, '').slice(INVITE_PATH_PREFIX.length);
  if (rest === '') return null;

  const segments = rest.split('/').filter((s) => s !== '');
  if (segments.length !== 1) return null;

  let token: string;
  try {
    token = decodeURIComponent(segments[0]!);
  } catch {
    // A malformed percent-encoding is not a token we can use.
    return null;
  }
  return token === '' ? null : token;
}

/**
 * Extract the token from an invitation landing, fragment first.
 *
 * `hash` is `window.location.hash`, which the browser hands over with its
 * leading `#` (and as `''` when there is none). The fragment is the shape
 * `email/delivery.ts` mints; the path segment is the legacy shape still in
 * inboxes. Fragment wins when both are present, because that is the one that
 * cannot have been copied out of a log.
 *
 * A `#` with nothing after it is not a token, and neither is a fragment that
 * carries anything but the token — `#a/b` or `#token=x` are not links we
 * generated, and guessing which part was meant would be inventing intent, the
 * same rule `inviteTokenFrom` applies to extra path segments.
 */
export function inviteTokenFromLocation(pathname: string, hash: string): string | null {
  if (!isInvitePath(pathname)) return null;

  const fragment = hash.startsWith('#') ? hash.slice(1) : hash;
  if (fragment !== '' && !fragment.includes('/') && !fragment.includes('&')) {
    let token: string;
    try {
      token = decodeURIComponent(fragment);
    } catch {
      // A malformed percent-encoding is not a token we can use. Fall through
      // to the path form rather than failing outright.
      token = '';
    }
    if (token !== '') return token;
  }

  return inviteTokenFrom(pathname);
}
